import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedDateTimeRange, FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { EventContentArg, formatRange } from '@fullcalendar/core'
import { addHours, isAfter, isBefore, isFuture, parseISO } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import {
  CLOSE_SESSION_BUTTON_TEXT,
  SESSION_INFO_TEXT,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import { CLOSE_SESSION_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/types'
import { isBookableEvent } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import {
  getPossessivePronoun,
  getReflexivePronoun,
  isAgeYoungerThanTeen,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import {
  getProgramDisplayLabel,
  getProgramNameFromId,
  isMinorProgram,
  isTeenProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { getAge } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import {
  AppointmentBookedDetails,
  BendEventExtendedProps,
  EventExtendedProps,
  GenericEventExtendedProps,
  isBendEvent,
  LyraEventExtendedProps,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { DEFAULT_VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { ProviderCalendarPopoverAppointmentBookedDetails } from './ProviderCalendarPopoverAppointmentBookedDetails'
import { ProviderCalendarPopoverDescription } from './ProviderCalendarPopoverDescription'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { IconButton } from '../../atoms/iconButton/IconButton'
import { AlignLeftIcon } from '../../atoms/icons/AlignLeftIcon'
import { ChatLiveIcon } from '../../atoms/icons/ChatLiveIcon'
import { CheckMarkInCircleIcon } from '../../atoms/icons/CheckMarkInCircleIcon'
import { ClipboardIcon } from '../../atoms/icons/ClipboardIcon'
import { ClipboardWithArrowIcon } from '../../atoms/icons/ClipboardWithArrowIcon'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { EditIcon } from '../../atoms/icons/EditIcon'
import { ExternalCalendarIcon } from '../../atoms/icons/ExternalCalendarIcon'
import { ExternalLinkIcon } from '../../atoms/icons/ExternalLinkIcon'
import { FamilySessionIcon } from '../../atoms/icons/FamilySessionIcon'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { InPersonStroke } from '../../atoms/icons/InPersonStroke'
import { LocationIcon } from '../../atoms/icons/LocationIcon'
import { OnsiteIcon } from '../../atoms/icons/OnsiteIcon'
import { PhoneIcon } from '../../atoms/icons/PhoneIcon'
import { Pills } from '../../atoms/icons/Pills'
import { ProfileCircleIcon } from '../../atoms/icons/ProfileCircleIcon'
import { ScheduleIcon } from '../../atoms/icons/ScheduleIcon'
import { TeenSessionIcon } from '../../atoms/icons/TeenSessionIcon'
import { TrashIcon } from '../../atoms/icons/TrashIcon'
import { VideoIcon } from '../../atoms/icons/VideoIcon'
import { Link } from '../../atoms/link/Link'
import { LoadingSkeleton } from '../../atoms/loadingSkeleton/LoadingSkeleton'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { Title } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { AppContext } from '../../context'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { InlineBanner } from '../inlineBanner/InlineBanner'
import { InlineWarningBanner } from '../inlineWarningBanner/InlineWarningBanner'
import { ProviderCalendarEventBadges } from '../providerCalendarEventBadges/ProviderCalendarEventBadges'

export type ProviderCalendarPopoverProps = {
  eventContentArg: EventContentArg
  title: string
  startDate: string
  endDate: string
  extendedProps: EventExtendedProps
  eventEndStr: string
  eventStartStr: string
  timeZone: string
  onClientProfilePressed?: (clientId: string) => void
  onClose?: () => void
  onDelete?: () => void
  onEdit?: () => void
  onOpenInGoogleCalendar?: (externalId: string, calendarConfigurationId: string) => void
  onOpenVideoConferencing?: (externalId: string, calendarConfigurationId: string) => void
  onOpenPopover?: ({ appointmentId, clientId }: { appointmentId: number; clientId: string }) => void
  onCancel?: (appointmentId: number) => void
  onCancelMosAppointment?: (appointmentId: number) => void
  onReschedule?: (appointmentId: number) => void
  onCloseSession?: (appointmentId: number, clientId: string | null) => void
  onCompleteNote?: (appointmentId: number, clientId: string) => void
  onViewCompletedNote?: (appointmentId: number) => void
  closeSessionType?: CLOSE_SESSION_TYPES
  hideCalendarEventDetails?: boolean
  isTemplate?: boolean
  hybridBookingMode?: boolean
  displayAppointmentBookedDetails?: boolean
  showRiskAlerts?: boolean
  getAppointmentBookedDetails?: (appointmentId: number) => Promise<AppointmentBookedDetails>
  onScheduleSession?: () => void
  showModalitySelection?: boolean
  isLegacyDAAppointment?: boolean
  engageDAProvider?: boolean
  supportsOnOffPlatformVideo?: boolean
  showLateCancellations?: boolean
  clientVoicemailConsent?: boolean
  guardianVoicemailConsent?: boolean
  guardianPhone?: string
  clientPronouns?: string[]
  isClientDataLoading?: boolean
  onOpenEditModality?: () => void
  enableSessionModalityEditability?: boolean
  conflictTooltipText?: string
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'flex-start',
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: '16px',
  boxShadow: `0 2px 16px ${theme.colors.shadowLow}`,
  paddingBottom: theme.spacing['24px'],
  width: '450px',
  gap: theme.spacing['16px'],
  maxHeight: '650px',
  overflow: 'auto',
}))

const HeaderButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'center',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const HeaderButton = styled(IconButton)({
  height: '24px',
  width: '24px',
})

const HeaderTopRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['16px'],
  justifyContent: 'space-between',
}))

const TitleBadgesContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  flex: 1,
}))

const Header = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['4px'],
  padding: `${theme.spacing['24px']} ${theme.spacing['24px']} ${theme.spacing['16px']} ${theme.spacing['24px']}`,
}))

const Section = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: `0px ${theme.spacing['24px']}`,
  gap: theme.spacing['16px'],
}))

const SectionItem = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['12px'],
}))

const SectionItemTopAligned = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing['12px'],
}))

const MeetingTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing['4px'],
}))

const TextColumn = styled.View({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
})

const JoinButton = styled(PrimaryButton)(({ theme }) => ({
  margin: `0px ${theme.spacing['24px']}`,
  flexGrow: 1,
}))

const HeadingTitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const MembersSectionContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  borderTopWidth: 1,
  borderTopColor: theme.colors.borderDefault,
  paddingTop: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const MembersSectionHeader = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['12px'],
}))

const MemberRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  paddingLeft: theme.spacing['32px'],
}))

const MemberInfo = styled.View({
  flexDirection: 'column',
})

const MemberPhoneContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

export const ProviderCalendarPopover: FunctionComponent<ProviderCalendarPopoverProps> = ({
  eventContentArg,
  title,
  startDate,
  endDate,
  extendedProps,
  eventEndStr,
  eventStartStr,
  timeZone,
  onClose,
  onClientProfilePressed,
  onDelete,
  onEdit,
  onOpenInGoogleCalendar,
  onOpenVideoConferencing,
  onOpenPopover,
  onCancel,
  onCancelMosAppointment,
  onReschedule,
  onCloseSession,
  onCompleteNote,
  onViewCompletedNote,
  closeSessionType,
  hideCalendarEventDetails = false,
  isTemplate,
  hybridBookingMode = false,
  displayAppointmentBookedDetails = false,
  showRiskAlerts,
  getAppointmentBookedDetails,
  onScheduleSession,
  showModalitySelection = false,
  isLegacyDAAppointment = false,
  engageDAProvider = false,
  supportsOnOffPlatformVideo = false,
  showLateCancellations = false,
  clientVoicemailConsent,
  guardianVoicemailConsent,
  guardianPhone: guardianPhoneFromEpisode,
  clientPronouns,
  isClientDataLoading,
  onOpenEditModality = () => {},
  enableSessionModalityEditability = false,
  conflictTooltipText,
}) => {
  const theme = useTheme() as ThemeType
  const intl = useIntl()
  const { formatMessage } = intl
  const { showIPCSessionCreate, isDAMinorProgramEnabled, isDAEngageOnOffPlatformVideoEnabled, isDBTTeensEnabled } =
    useFlags()
  const { showCalendarScheduleSession } = useContext(AppContext)
  const [isLoadingAppointmentDetails, setIsLoadingAppointmentDetails] = useState(false)
  const appointmentDetailsFetchAttempts = useRef(new Set<number>())
  const eventType = extendedProps.lyraEventType
  const isLyraSession = eventType === 'session' && !isBendEvent(extendedProps)
  const shouldShowAppointmentAddress =
    showModalitySelection &&
    isLyraSession &&
    extendedProps?.appointmentMeetingFormat === MEETING_FORMATS.IN_PERSON &&
    extendedProps?.appointmentAddress

  const programName = isLyraSession ? getProgramNameFromId(extendedProps.lyraProgramId) : undefined
  const programNameLabel = programName
    ? getProgramDisplayLabel(intl, programName, ['daClientList', 'short', 'default'])
    : ''
  const programNameAgeGroup = programName ? (Programs.getProgramConfigByName(programName)?.ageGroup ?? '') : ''
  const isDbtTeensSession = isDBTTeensEnabled && programName === ProgramNames.DialecticalBehaviorTherapyTeens
  const isMinorProgramAndFlagEnabled = programName ? isMinorProgram(programName) && isDAMinorProgramEnabled : false
  const lyraExtendedProps = extendedProps as LyraEventExtendedProps
  const bookedDetails = lyraExtendedProps?.appointmentBookedDetails
  const dbtSessionType: string =
    (lyraExtendedProps.sessionType?.startsWith('dbt_teens_') &&
    lyraExtendedProps.sessionType !== LTSessionTypes.DBT_TEENS_SESSION
      ? lyraExtendedProps.sessionType
      : undefined) ??
    (lyraExtendedProps.appointmentSessionNumber === 1
      ? LTSessionTypes.DBT_TEENS_INTAKE_SESSION
      : LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION)
  const isDbtIntake = dbtSessionType === LTSessionTypes.DBT_TEENS_INTAKE_SESSION
  const showDbtAttendees =
    isDbtIntake ||
    dbtSessionType === LTSessionTypes.DBT_TEENS_FAMILY_SESSION ||
    dbtSessionType === LTSessionTypes.DBT_TEENS_MEDICATION_INITIAL_SESSION ||
    dbtSessionType === LTSessionTypes.DBT_TEENS_MEDICATION_FOLLOWUP_SESSION
  const primaryGuardianDetails = isDbtTeensSession ? bookedDetails : lyraExtendedProps
  const fallbackGuardianDetails = isDbtTeensSession ? lyraExtendedProps : bookedDetails
  const resolvedGuardianLyraId = primaryGuardianDetails?.guardianLyraId ?? fallbackGuardianDetails?.guardianLyraId
  const resolvedGuardianFirstName =
    primaryGuardianDetails?.guardianFirstName ?? fallbackGuardianDetails?.guardianFirstName ?? ''
  const resolvedGuardianLastName =
    primaryGuardianDetails?.guardianLastName ?? fallbackGuardianDetails?.guardianLastName ?? ''
  const resolvedGuardianPhone =
    primaryGuardianDetails?.guardianPhone ?? fallbackGuardianDetails?.guardianPhone ?? guardianPhoneFromEpisode
  const resolvedChildFirstName =
    lyraExtendedProps?.childFirstName ??
    bookedDetails?.childFirstName ??
    (isDbtTeensSession ? lyraExtendedProps.clientFirstName : '')
  const computedAge =
    bookedDetails?.clientDob != null ? getAge({ date: bookedDetails.clientDob.replace('DOB: ', '') }) : NaN
  const clientAge = !isNaN(computedAge) ? computedAge : undefined
  const isDAFamily = programName === ProgramNames.DirectAccessFamilyTherapy
  const isMinorProgramForChildren = programName ? isMinorProgram(programName) && !isTeenProgram(programName) : false
  const isClientChildAge =
    isMinorProgramForChildren || (isDAFamily && clientAge != null && isAgeYoungerThanTeen(clientAge))
  const isDAFamilyMinor = isDAFamily && clientAge != null && clientAge < 18
  const isDAFamilyTeenLed =
    isDAFamily && clientAge != null && clientAge >= 13 && clientAge < 18 && !resolvedGuardianLyraId
  const isParentLedBooking = (isMinorProgramAndFlagEnabled || isDAFamilyMinor) && !!resolvedGuardianLyraId
  const isTeenLedBooking =
    (programName ? isTeenProgram(programName) && isDAMinorProgramEnabled && !isParentLedBooking : false) ||
    isDAFamilyTeenLed
  const shouldShowMembersSection = isDbtTeensSession ? showDbtAttendees : isParentLedBooking
  const showMembersSkeleton = shouldShowMembersSection && (isLoadingAppointmentDetails || !!isClientDataLoading)
  const date = useMemo(() => new Date(startDate), [startDate])
  const isInPast = isBefore(date, new Date())
  const showActionsButtonsForSlotBelow = !isInPast && !isTemplate && showIPCSessionCreate && showCalendarScheduleSession
  const canEditModality =
    enableSessionModalityEditability && isLyraSession && !isInPast && isDAEngageOnOffPlatformVideoEnabled

  useEffect(() => {
    if (isLyraSession) {
      const { clientId, lyraAppointmentId } = extendedProps
      onOpenPopover &&
        clientId &&
        lyraAppointmentId &&
        onOpenPopover({
          clientId: clientId,
          appointmentId: lyraAppointmentId,
        })
    }
  }, [eventType, extendedProps, onOpenPopover, isLyraSession])

  useEffect(() => {
    const clientExtendedProps = extendedProps as LyraEventExtendedProps
    if (
      (hybridBookingMode || (isDbtTeensSession && showDbtAttendees)) &&
      clientExtendedProps.lyraAppointmentId &&
      eventContentArg.event.extendedProps.appointmentBookedDetails === undefined &&
      !appointmentDetailsFetchAttempts.current.has(clientExtendedProps.lyraAppointmentId) &&
      getAppointmentBookedDetails
    ) {
      appointmentDetailsFetchAttempts.current.add(clientExtendedProps.lyraAppointmentId)
      setIsLoadingAppointmentDetails(true)
      getAppointmentBookedDetails(clientExtendedProps.lyraAppointmentId)
        .then((data) => {
          eventContentArg.event.setExtendedProp('appointmentBookedDetails', data)
        })
        .catch(() => undefined)
        .finally(() => setIsLoadingAppointmentDetails(false))
    }
  }, [
    eventContentArg.event,
    extendedProps,
    getAppointmentBookedDetails,
    hybridBookingMode,
    isDbtTeensSession,
    showDbtAttendees,
  ])

  const hybridProviderEventTitle = useMemo(() => {
    if (eventType === 'bookable' && !isTemplate) {
      return title + ' (one-time)'
    }

    if (!isLyraSession) {
      return title
    }

    if (showLateCancellations && (lyraExtendedProps.isCoveredLateCancel || lyraExtendedProps.isClientOwedLateCancel)) {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastName} • Cancelled • {programName}'
          description='Event title for late cancelled lyra appointment'
          values={{
            firstName: lyraExtendedProps.clientFirstName,
            lastName: lyraExtendedProps.clientLastName,
            programName: programNameLabel,
          }}
        />
      )
    }
    if (lyraExtendedProps.isNoShow) {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastName} • No Show • {programName}'
          description='Event title for lyra appointment'
          values={{
            firstName: lyraExtendedProps.clientFirstName,
            lastName: lyraExtendedProps.clientLastName,
            programName: programNameLabel,
          }}
        />
      )
    }
    if (isLegacyDAAppointment && lyraExtendedProps.providerScheduled) {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastName}'
          description='Event title for legacy DA follow-up appointment'
          values={{
            firstName: lyraExtendedProps.clientFirstName,
            lastName: lyraExtendedProps.clientLastName,
          }}
        />
      )
    }
    if (lyraExtendedProps.appointmentClass === 'initial') {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastName} • Intake {programName}'
          description='Event title for lyra appointment'
          values={{
            firstName: lyraExtendedProps.clientFirstName,
            lastName: lyraExtendedProps.clientLastName,
            programName: !hideCalendarEventDetails ? `• ${programNameLabel}` : '',
          }}
        />
      )
    }
    return (
      <FormattedMessage
        defaultMessage='{firstName} {lastName} • Session {sessionNumber} • {programName}'
        description='Event title for lyra appointment'
        values={{
          firstName: lyraExtendedProps.clientFirstName,
          lastName: lyraExtendedProps.clientLastName,
          sessionNumber: lyraExtendedProps.appointmentSessionNumber,
          programName: programNameLabel,
        }}
      />
    )
  }, [
    eventType,
    isTemplate,
    showLateCancellations,
    lyraExtendedProps.isCoveredLateCancel,
    lyraExtendedProps.isClientOwedLateCancel,
    lyraExtendedProps.isNoShow,
    lyraExtendedProps.appointmentClass,
    lyraExtendedProps.clientFirstName,
    lyraExtendedProps.clientLastName,
    lyraExtendedProps.appointmentSessionNumber,
    lyraExtendedProps.providerScheduled,
    programNameLabel,
    title,
    hideCalendarEventDetails,
    isLegacyDAAppointment,
    isLyraSession,
  ])

  const meetingFormatIcon = useMemo(() => {
    if (!isLyraSession) {
      return <ScheduleIcon fillColor={theme.colors.iconDefault} size={24} />
    }
    switch (extendedProps.appointmentMeetingFormat) {
      case MEETING_FORMATS.IN_PERSON: {
        return shouldShowAppointmentAddress ? (
          <OnsiteIcon fillColor={theme.colors.iconDefault} size={24} />
        ) : (
          <LocationIcon fillColor={theme.colors.iconDefault} size={24} />
        )
      }
      case MEETING_FORMATS.PHONE:
      case MEETING_FORMATS.CALL: {
        return <PhoneIcon fillColor={theme.colors.iconDefault} size={24} />
      }
      case MEETING_FORMATS.LIVE_MESSAGING: {
        return <ChatLiveIcon fillColor={theme.colors.iconDefault} size={24} />
      }
      default: {
        return <VideoIcon fillColor={theme.colors.iconDefault} size={24} />
      }
    }
  }, [extendedProps, theme.colors.iconDefault, shouldShowAppointmentAddress, isLyraSession])

  const dbtSessionTypePresentation = useMemo(() => {
    if (isDbtIntake) return undefined
    switch (dbtSessionType) {
      case LTSessionTypes.DBT_TEENS_SESSION:
      case LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION:
        return {
          icon: <TeenSessionIcon fillColor={theme.colors.iconDefault} height={24} width={21} />,
          label: <FormattedMessage defaultMessage='Teen session' description='DBT Teens teen session type' />,
        }
      case LTSessionTypes.DBT_TEENS_FAMILY_SESSION:
        return {
          icon: <FamilySessionIcon fillColor={theme.colors.iconDefault} height={24} width={24} />,
          label: <FormattedMessage defaultMessage='Family session' description='DBT Teens family session type' />,
        }
      case LTSessionTypes.DBT_TEENS_MEDICATION_INITIAL_SESSION:
      case LTSessionTypes.DBT_TEENS_MEDICATION_FOLLOWUP_SESSION:
        return {
          icon: <Pills fillColor={theme.colors.iconDefault} size={24} />,
          label: (
            <FormattedMessage
              defaultMessage='Medication management session'
              description='DBT Teens medication management session type'
            />
          ),
        }
      default:
        return undefined
    }
  }, [dbtSessionType, isDbtIntake, theme.colors.iconDefault])

  const shouldShowClientContact = !shouldShowMembersSection

  const sessionActionInfo =
    closeSessionType && closeSessionType in SESSION_INFO_TEXT
      ? SESSION_INFO_TEXT[closeSessionType as keyof typeof SESSION_INFO_TEXT]
      : undefined

  const closeSessionButtonText =
    closeSessionType && closeSessionType in CLOSE_SESSION_BUTTON_TEXT
      ? CLOSE_SESSION_BUTTON_TEXT[closeSessionType as keyof typeof CLOSE_SESSION_BUTTON_TEXT]
      : undefined

  const isLateCancel =
    showLateCancellations &&
    isLyraSession &&
    (lyraExtendedProps.isCoveredLateCancel || lyraExtendedProps.isClientOwedLateCancel)

  const handleCancelClick = useCallback(() => {
    onCancel && onCancel((extendedProps as LyraEventExtendedProps).lyraAppointmentId)
  }, [extendedProps, onCancel])

  const handleCancelMosAppointmentClick = useCallback(() => {
    onCancelMosAppointment?.((extendedProps as LyraEventExtendedProps).lyraAppointmentId)
  }, [extendedProps, onCancelMosAppointment])

  const handleRescheduleClick = useCallback(() => {
    onReschedule && onReschedule((extendedProps as LyraEventExtendedProps).lyraAppointmentId)
  }, [extendedProps, onReschedule])

  const handleCloseSessionClick = useCallback(() => {
    const { lyraAppointmentId, clientId } = extendedProps as LyraEventExtendedProps
    switch (closeSessionType) {
      case CLOSE_SESSION_TYPES.NOTE_NEEDED:
        onCompleteNote && onCompleteNote(lyraAppointmentId, clientId ?? '')
        break
      case CLOSE_SESSION_TYPES.NOTE_AND_INVOICE_COMPLETED:
        onViewCompletedNote && onViewCompletedNote(lyraAppointmentId)
        break
      case CLOSE_SESSION_TYPES.INVOICE_NEEDED:
      case CLOSE_SESSION_TYPES.INVOICE_AND_NOTE_NEEDED:
      case CLOSE_SESSION_TYPES.SESSION_TO_CLOSE:
        onCloseSession && onCloseSession(lyraAppointmentId, clientId)
        break
    }
  }, [closeSessionType, extendedProps, onCloseSession, onCompleteNote, onViewCompletedNote])

  const renderGenericSections = useCallback(
    (props: GenericEventExtendedProps | BendEventExtendedProps) => {
      if (props.isPrivateEvent) {
        return null
      }
      return (
        <>
          {props.description && (
            <SectionItem testID={tID('ProviderCalendarPopover-sectionItem-description')}>
              <View style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                <AlignLeftIcon size={24} fillColor={theme.colors.iconDefault} />
              </View>
              <ProviderCalendarPopoverDescription description={props.description} />
            </SectionItem>
          )}
          {!hideCalendarEventDetails && (
            <SectionItem testID={tID('ProviderCalendarPopover-sectionItem-externalLink')}>
              <ExternalLinkIcon size={24} strokeColor={theme.colors.iconDefault} />
              <Link
                testID={tID('ProviderCalendarPopover-open-in-google-calendar-link')}
                size={BodyTextSize.SMALL}
                underline
                onPress={() => onOpenInGoogleCalendar?.(props.externalId, props.calendarConfigurationId)}
                text={
                  <FormattedMessage
                    defaultMessage='Open Google Calendar'
                    description='Link to event in google calendar'
                  />
                }
              />
            </SectionItem>
          )}
          {hybridBookingMode && props.calendarName && (
            <SectionItem>
              <ExternalCalendarIcon fillColor={theme.colors.iconDefault} />
              <BodyText text={props.calendarName} size={BodyTextSize.SMALL} />
            </SectionItem>
          )}
        </>
      )
    },
    [theme.colors.iconDefault, hideCalendarEventDetails, hybridBookingMode, onOpenInGoogleCalendar],
  )

  const sections = useMemo(() => {
    if (isBendEvent(extendedProps)) {
      return renderGenericSections(extendedProps)
    }

    switch (eventType) {
      case 'bookable':
      case 'bookable_recurring':
        if (!showActionsButtonsForSlotBelow) {
          return null
        }
        return (
          <PrimaryButton
            testID={tID('ProviderCalendarPopover-schedule-session-button')}
            text={<FormattedMessage defaultMessage='Schedule session' description='Button to schedule session' />}
            onPress={() => onScheduleSession?.()}
            fullWidth
          />
        )
      case 'generic':
        return renderGenericSections(extendedProps)
      case 'session':
        const { appointmentBookedDetails } = extendedProps
        const isAtLeast24HoursFromNow = isAfter(date, addHours(new Date(), 24))
        const membersSectionHeader = isDbtTeensSession ? (
          <FormattedMessage defaultMessage='Attendees' description='DBT Teens attendees section header' />
        ) : (
          <FormattedMessage defaultMessage='Members' description='Members section header' />
        )
        return (
          <>
            {displayAppointmentBookedDetails ? (
              <>
                <ProviderCalendarPopoverAppointmentBookedDetails
                  appointmentBookedDetails={appointmentBookedDetails}
                  showRiskAlerts={showRiskAlerts}
                  isLoadingAppointmentDetails={isLoadingAppointmentDetails}
                  showModalitySelection={showModalitySelection}
                  appointmentAddress={extendedProps.appointmentAddress}
                  appointmentMeetingFormat={extendedProps.appointmentMeetingFormat}
                />
                {isFuture(new Date(startDate)) &&
                  onCancelMosAppointment &&
                  !isLateCancel &&
                  appointmentBookedDetails?.isMos && (
                    <SectionItem testID={tID('ProviderCalendarPopover-cancelMosAppointmentAction')}>
                      <SecondaryButton
                        onPress={handleCancelMosAppointmentClick}
                        customTextColor={theme.colors.primaryButtonBackgroundDefault}
                        style={{
                          borderWidth: 1,
                          borderStyle: 'solid',
                          maxWidth: '100%',
                          flex: 1,
                        }}
                        text={
                          <FormattedMessage
                            defaultMessage='Cancel'
                            description='Button that begins canceling a MOS appointment from the provider calendar'
                          />
                        }
                        testID={tID('ProviderCalendarPopover-cancel-mos-appointment-button')}
                      />
                    </SectionItem>
                  )}
              </>
            ) : (
              <>
                {extendedProps.appointmentMeetingFormat &&
                  (!hideCalendarEventDetails || shouldShowAppointmentAddress) && (
                    <SectionItem testID={tID('ProviderCalendarPopover-sessionModality')}>
                      {meetingFormatIcon}
                      <MeetingTextContainer>
                        <BodyText
                          text={
                            shouldShowAppointmentAddress ? (
                              extendedProps.appointmentAddress
                            ) : isDAEngageOnOffPlatformVideoEnabled &&
                              supportsOnOffPlatformVideo &&
                              extendedProps.appointmentMeetingFormat === MEETING_FORMATS.VIDEO ? (
                              <FormattedMessage
                                defaultMessage='Video session ({platformName})'
                                values={{
                                  platformName:
                                    CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[
                                      lyraExtendedProps.appointmentVideoPlatform ?? DEFAULT_VIDEO_PLATFORM
                                    ],
                                }}
                                description='Video session with platform name displayed on event'
                              />
                            ) : (
                              <FormattedMessage
                                defaultMessage='{appointmentMeetingFormat, select,
                                inPerson {In-person}
                                phone {Phone}
                                call {Phone}
                                live_messaging {Live messaging}
                                other {Video}
                                } session'
                                values={{
                                  appointmentMeetingFormat: extendedProps.appointmentMeetingFormat,
                                }}
                                description='Client timezone displayed on event'
                              />
                            )
                          }
                          size={BodyTextSize.SMALL}
                        />
                        {canEditModality && (
                          <>
                            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                            <BodyText text={' · '} size={BodyTextSize.SMALL} />
                            <Link
                              text={
                                <FormattedMessage
                                  defaultMessage='Edit'
                                  description='Link to edit the session modality'
                                />
                              }
                              onPress={onOpenEditModality}
                              size={BodyTextSize.SMALL}
                              testID={tID('ProviderCalendarPopover-editModality-link')}
                            />
                          </>
                        )}
                      </MeetingTextContainer>
                    </SectionItem>
                  )}
                {isDbtTeensSession && dbtSessionTypePresentation && (
                  <SectionItem testID={tID('ProviderCalendarPopover-dbt-session-type')}>
                    {dbtSessionTypePresentation.icon}
                    <BodyText text={dbtSessionTypePresentation.label} size={BodyTextSize.SMALL} />
                  </SectionItem>
                )}
                {shouldShowClientContact && extendedProps.clientPhoneNumber && (
                  <SectionItemTopAligned testID={tID('ProviderCalendarPopover-clientContactNumber')}>
                    <PhoneIcon size={24} fillColor={theme.colors.iconDefault} />
                    <TextColumn>
                      <MemberPhoneContainer>
                        <BodyText
                          text={formatPhoneNumberForDisplay(extendedProps.clientPhoneNumber)}
                          size={BodyTextSize.SMALL}
                          color={theme.colors.textActive}
                        />
                        {!isClientDataLoading && clientVoicemailConsent && (
                          <Tooltip
                            placement='top'
                            hoverEnabled
                            accessibilityLabel='Voicemail consent indicator'
                            testID={tID('ProviderCalendarPopover-voicemailConsent')}
                            content={
                              <FormattedMessage
                                defaultMessage='{clientFirstName} provided voicemail consent'
                                description='Tooltip message indicating that the client has provided voicemail consent'
                                values={{ clientFirstName: extendedProps.clientFirstName }}
                              />
                            }
                          >
                            <CheckMarkInCircleIcon size={20} />
                          </Tooltip>
                        )}
                      </MemberPhoneContainer>
                      {!hideCalendarEventDetails && (
                        <BodyText
                          text={
                            <FormattedMessage
                              defaultMessage='Dial *67 to block your number'
                              description='Instruction to block caller ID'
                            />
                          }
                          size={BodyTextSize.SMALL}
                        />
                      )}
                    </TextColumn>
                  </SectionItemTopAligned>
                )}
                {sessionActionInfo && !isLegacyDAAppointment && (
                  <SectionItem testID={tID('ProviderCalendarPopover-sessionActionInfo')}>
                    {closeSessionType === CLOSE_SESSION_TYPES.INVOICE_NEEDED_BY_ADMIN ||
                    closeSessionType === CLOSE_SESSION_TYPES.NOTE_NEEDED_BY_PROVIDER ? (
                      <ClipboardWithArrowIcon size={24} color={theme.colors.iconDefault} />
                    ) : (
                      <ClipboardIcon size={24} color={theme.colors.iconDefault} />
                    )}
                    <BodyText text={<FormattedMessage {...sessionActionInfo} />} size={BodyTextSize.SMALL} />
                  </SectionItem>
                )}
                {showLateCancellations && isLyraSession && lyraExtendedProps.isClientOwedLateCancel && (
                  <InlineBanner
                    testId='ProviderCalendarPopover-clientOwedMessage'
                    backgroundColor={theme.colors.backgroundSection}
                    textColor={theme.colors.textSecondary}
                    icon={<InfoIcon size={24} fillColor={theme.colors.textSecondary} />}
                    text={
                      <FormattedMessage
                        defaultMessage='Client has no covered late cancellations remaining. Please contact the client directly if insufficient notice given.'
                        description='Message shown when client has exhausted their covered late cancellations'
                      />
                    }
                    textSize={BodyTextSize.SMALL}
                    marginBottom='0px'
                  />
                )}
                {hybridBookingMode &&
                  lyraExtendedProps.clientId &&
                  !hideCalendarEventDetails &&
                  !isLegacyDAAppointment && (
                    <SectionItem testID={tID('ProviderCalendarPopover-sectionItem-profileClient')}>
                      <ProfileCircleIcon size={24} fillColor={theme.colors.iconDefault} />
                      <Link
                        testID={tID('ProviderCalendarPopover-client-details-link')}
                        size={BodyTextSize.SMALL}
                        underline
                        onPress={() => onClientProfilePressed?.(lyraExtendedProps.clientId as string)}
                        text={<FormattedMessage defaultMessage='Client details' description='Link to client details' />}
                      />
                    </SectionItem>
                  )}
              </>
            )}
            {isTeenLedBooking &&
              extendedProps.appointmentClass === 'initial' &&
              appointmentBookedDetails?.clientDob && (
                <InlineWarningBanner
                  testId='ProviderCalendarPopover-teenLedBookingBanner'
                  textSize={BodyTextSize.SMALL}
                  marginBottom='0px'
                  text={
                    <FormattedMessage
                      defaultMessage='<b>{firstName} (teen/{age}) booked care {reflexivePronoun}. Would you like to involve {possessivePronoun} caregiver?</b> If so, message {firstName} to discuss and confirm the caregiver’s contact information.'
                      description='Teen-led booking banner informing provider that teen booked care themselves'
                      values={{
                        b: (chunks) => <strong>{chunks}</strong>,
                        firstName: extendedProps.clientFirstName ?? '',
                        age: getAge({ date: appointmentBookedDetails.clientDob.replace('DOB: ', '') }),
                        reflexivePronoun: getReflexivePronoun(clientPronouns),
                        possessivePronoun: getPossessivePronoun(clientPronouns),
                      }}
                    />
                  }
                />
              )}
            {showMembersSkeleton && (
              <MembersSectionContainer testID={tID('ProviderCalendarPopover-membersSkeleton')}>
                <MembersSectionHeader>
                  <InPersonStroke size='24' fillColor={theme.colors.iconDefault} />
                  <BodyText text={membersSectionHeader} size={BodyTextSize.SMALL} />
                </MembersSectionHeader>
                <LoadingSkeleton width={200} height={14} marginTop={4} />
                <LoadingSkeleton width={160} height={14} marginTop={8} />
              </MembersSectionContainer>
            )}
            {shouldShowMembersSection && !isLoadingAppointmentDetails && !isClientDataLoading && (
              <MembersSectionContainer testID={tID('ProviderCalendarPopover-membersSection')}>
                <MembersSectionHeader>
                  <InPersonStroke size='24' fillColor={theme.colors.iconDefault} />
                  <BodyText text={membersSectionHeader} size={BodyTextSize.SMALL} />
                </MembersSectionHeader>
                <MemberRow testID={tID('ProviderCalendarPopover-memberRow-client')}>
                  <MemberInfo>
                    {(() => {
                      const firstName = resolvedChildFirstName
                      const age = appointmentBookedDetails?.clientDob
                        ? getAge({ date: appointmentBookedDetails.clientDob.replace('DOB: ', '') })
                        : null
                      const isValidAge = age !== null && !isNaN(age)
                      return (
                        <BodyText
                          text={
                            isValidAge ? (
                              <FormattedMessage
                                defaultMessage='{firstName} (age {age})'
                                description='Child or teen client name with age'
                                values={{ firstName, age }}
                              />
                            ) : (
                              firstName
                            )
                          }
                          size={BodyTextSize.SMALL}
                          testID={tID('ProviderCalendarPopover-clientName')}
                        />
                      )
                    })()}
                    <BodyText
                      text={
                        isClientChildAge ? (
                          <FormattedMessage defaultMessage='Child' description='Child client type label' />
                        ) : (
                          <FormattedMessage defaultMessage='Teen' description='Teen client type label' />
                        )
                      }
                      size={BodyTextSize.CAPTION}
                      color={theme.colors.textSecondary}
                      testID={tID('ProviderCalendarPopover-clientTypeLabel')}
                    />
                  </MemberInfo>
                  {(() => {
                    // LCT Teens: Use phone parent entered for teen at booking
                    // DA Teens: Use phone from parent intake once completed
                    // DA Children: No phone number displayed
                    if (isClientChildAge) return null
                    const teenPhone =
                      programName === ProgramNames.DirectAccessIndividualAdolescentTherapy
                        ? appointmentBookedDetails?.childPhone
                        : extendedProps?.clientPhoneNumber
                    return (
                      teenPhone && (
                        <MemberPhoneContainer>
                          <BodyText
                            text={formatPhoneNumberForDisplay(teenPhone)}
                            size={BodyTextSize.SMALL}
                            color={theme.colors.textActive}
                            testID={tID('ProviderCalendarPopover-clientPhone')}
                          />
                        </MemberPhoneContainer>
                      )
                    )
                  })()}
                </MemberRow>
                <MemberRow testID={tID('ProviderCalendarPopover-memberRow-caregiver')}>
                  <MemberInfo>
                    <BodyText
                      text={
                        <FormattedMessage
                          defaultMessage='{firstName} {lastName}'
                          description='Caregiver full name'
                          values={{
                            firstName: resolvedGuardianFirstName,
                            lastName: resolvedGuardianLastName,
                          }}
                        />
                      }
                      size={BodyTextSize.SMALL}
                      testID={tID('ProviderCalendarPopover-caregiverName')}
                    />
                    <BodyText
                      text={<FormattedMessage defaultMessage='Caregiver' description='Caregiver label' />}
                      size={BodyTextSize.CAPTION}
                      color={theme.colors.textSecondary}
                      testID={tID('ProviderCalendarPopover-caregiverLabel')}
                    />
                  </MemberInfo>
                  <MemberPhoneContainer>
                    {resolvedGuardianPhone && (
                      <BodyText
                        text={formatPhoneNumberForDisplay(resolvedGuardianPhone)}
                        size={BodyTextSize.SMALL}
                        color={theme.colors.textActive}
                        testID={tID('ProviderCalendarPopover-caregiverPhone')}
                      />
                    )}
                    {!isClientDataLoading && guardianVoicemailConsent && (
                      <Tooltip
                        placement='top'
                        hoverEnabled
                        accessibilityLabel='Voicemail consent indicator'
                        testID={tID('ProviderCalendarPopover-voicemailConsent-caregiver')}
                        content={
                          <FormattedMessage
                            defaultMessage='{clientFirstName} provided voicemail consent'
                            description='Tooltip message indicating that the caregiver has provided voicemail consent'
                            values={{ clientFirstName: resolvedGuardianFirstName }}
                          />
                        }
                      >
                        <CheckMarkInCircleIcon size={20} />
                      </Tooltip>
                    )}
                  </MemberPhoneContainer>
                </MemberRow>
              </MembersSectionContainer>
            )}
            {!isLegacyDAAppointment && (
              <SectionItem testID={tID('ProviderCalendarPopover-sessionModalityActions')}>
                {isFuture(new Date(startDate)) && onCancel && !isLateCancel && (
                  <SecondaryButton
                    onPress={handleCancelClick}
                    customTextColor={theme.colors.primaryButtonBackgroundDefault}
                    style={{
                      borderWidth: 1,
                      borderStyle: 'solid',
                      maxWidth: '100%',
                      flex: 1,
                    }}
                    text={
                      <FormattedMessage defaultMessage='Cancel' description='Button that cancels this calendar event' />
                    }
                    testID={tID('ProviderCalendarPopover-cancel-button')}
                  />
                )}
                {isAtLeast24HoursFromNow && onReschedule && !isLateCancel && (
                  <PrimaryButton
                    onPress={handleRescheduleClick}
                    style={{ flex: 1 }}
                    text={
                      <FormattedMessage
                        defaultMessage='Reschedule'
                        description='Button that reschedules this calendar event'
                      />
                    }
                    testID={tID('ProviderCalendarPopover-reschedule-button')}
                  />
                )}
                {(isInPast || isLateCancel) &&
                  onCloseSession &&
                  closeSessionButtonText &&
                  (closeSessionType === CLOSE_SESSION_TYPES.NOTE_AND_INVOICE_COMPLETED ? (
                    <SecondaryButton
                      onPress={handleCloseSessionClick}
                      style={{ flex: 1 }}
                      text={<FormattedMessage {...closeSessionButtonText} />}
                      testID={tID('ProviderCalendarPopover-view-note-button')}
                    />
                  ) : (
                    <PrimaryButton
                      onPress={handleCloseSessionClick}
                      style={{ flex: 1 }}
                      text={<FormattedMessage {...closeSessionButtonText} />}
                      testID={tID('ProviderCalendarPopover-close-session-button')}
                    />
                  ))}
              </SectionItem>
            )}
          </>
        )
    }
  }, [
    eventType,
    showActionsButtonsForSlotBelow,
    extendedProps,
    theme.colors.iconDefault,
    theme.colors.textActive,
    theme.colors.backgroundSection,
    theme.colors.textSecondary,
    theme.colors.primaryButtonBackgroundDefault,
    hideCalendarEventDetails,
    hybridBookingMode,
    date,
    displayAppointmentBookedDetails,
    showRiskAlerts,
    isLoadingAppointmentDetails,
    showModalitySelection,
    shouldShowAppointmentAddress,
    meetingFormatIcon,
    isClientDataLoading,
    clientVoicemailConsent,
    dbtSessionTypePresentation,
    isDbtTeensSession,
    shouldShowClientContact,
    shouldShowMembersSection,
    sessionActionInfo,
    isLegacyDAAppointment,
    closeSessionType,
    showLateCancellations,
    programName,
    clientPronouns,
    isClientChildAge,
    startDate,
    onCancel,
    isLateCancel,
    handleCancelClick,
    onCancelMosAppointment,
    handleCancelMosAppointmentClick,
    onReschedule,
    handleRescheduleClick,
    isInPast,
    onCloseSession,
    closeSessionButtonText,
    handleCloseSessionClick,
    onScheduleSession,
    onClientProfilePressed,
    resolvedChildFirstName,
    resolvedGuardianFirstName,
    resolvedGuardianLastName,
    resolvedGuardianPhone,
    isTeenLedBooking,
    showMembersSkeleton,
    guardianVoicemailConsent,
    lyraExtendedProps.isClientOwedLateCancel,
    lyraExtendedProps.clientId,
    lyraExtendedProps.appointmentVideoPlatform,
    canEditModality,
    onOpenEditModality,
    isLyraSession,
    renderGenericSections,
    isDAEngageOnOffPlatformVideoEnabled,
    supportsOnOffPlatformVideo,
  ])
  const isBookable = isBookableEvent(extendedProps)
  const inPersonLoc = extendedProps.modality_availability?.in_person?.location
  const inPersonFullAddress = inPersonLoc
    ? (() => {
        const street = inPersonLoc.street1 || ''
        const cityState = [inPersonLoc.city, inPersonLoc.state].filter(Boolean).join(', ')
        const zip = inPersonLoc.zipcode
        const cityStateZip = [cityState, zip].filter(Boolean).join(' ')
        return [street, cityStateZip].filter(Boolean).join('\n')
      })()
    : ''

  const clientNameText = (
    <FormattedMessage
      defaultMessage='{firstName} {lastName}'
      description='Client full name displayed in calendar event'
      values={{
        firstName: isLyraSession ? extendedProps.clientFirstName : '',
        lastName: isLyraSession ? extendedProps.clientLastName : '',
      }}
    />
  )

  const { appointmentBookedDetails, clientTimeZone, clientFirstName } = extendedProps as LyraEventExtendedProps
  return (
    <>
      <Container
        testID={tID(`ProviderCalendarPopover-container${isLyraSession ? '-' + extendedProps.lyraAppointmentId : ''}`)}
      >
        <Header testID={tID('ProviderCalendarPopover-header')}>
          <HeaderTopRow>
            <TitleBadgesContainer>
              {!(isLyraSession && extendedProps.clientId && !hideCalendarEventDetails) && (
                <Title
                  testID={tID('ProviderCalendarPopover-event-title')}
                  size={TitleSize.MEDIUM}
                  text={hybridProviderEventTitle}
                />
              )}
              <ProviderCalendarEventBadges
                title={title}
                extendedProps={extendedProps}
                hybridBookingMode={hybridBookingMode}
                programName={programName}
                engageDAProvider={engageDAProvider}
                appointmentBookedDetails={appointmentBookedDetails}
                isDAMinorProgramEnabled={isDAMinorProgramEnabled}
                programNameAgeGroup={programNameAgeGroup}
                conflictTooltipText={conflictTooltipText}
              />
            </TitleBadgesContainer>
            <HeaderButtonContainer>
              {isBookable && (
                <>
                  <HeaderButton
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Edit',
                      description: 'Button for editing an availability on provider calendar or template',
                    })}
                    onPress={() => onEdit?.()}
                    iconColor={theme.colors.iconDefault}
                    leftIcon={<EditIcon fillColor={theme.colors.iconDefault} size={20} isFilled={false} />}
                    tabIndex={0}
                    testID={tID('ProviderCalendarPopover-edit-button')}
                  />
                  <HeaderButton
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Delete',
                      description: 'Button for deleting an availability on provider calendar or template',
                    })}
                    onPress={() => onDelete?.()}
                    iconColor={theme.colors.iconDefault}
                    leftIcon={<TrashIcon size={20} />}
                    tabIndex={0}
                    testID={tID('ProviderCalendarPopover-delete-button')}
                  />
                </>
              )}
              <HeaderButton
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Close',
                  description: 'Button for closing the availability popover on provider calendar or template',
                })}
                onPress={() => onClose?.()}
                iconColor={theme.colors.iconDefault}
                leftIcon={<CloseIcon size={16} />}
                tabIndex={0}
                testID={tID('ProviderCalendarPopover-close-button')}
              />
            </HeaderButtonContainer>
          </HeaderTopRow>
          {isLyraSession && extendedProps.clientId && !hideCalendarEventDetails && (
            <HeadingTitleContainer>
              {isLegacyDAAppointment ? (
                <Title
                  testID={tID('ProviderCalendarPopover-client-name')}
                  size={TitleSize.MEDIUM}
                  text={clientNameText}
                />
              ) : (
                <Link
                  onPress={() => onClientProfilePressed?.(extendedProps.clientId as string)}
                  accessibilityLabel={formatMessage(
                    {
                      defaultMessage: 'View profile for {firstName} {lastName}',
                      description: 'Accessibility label for client name link at top of calendar popover',
                    },
                    {
                      firstName: extendedProps.clientFirstName,
                      lastName: extendedProps.clientLastName,
                    },
                  )}
                  text={
                    <Title
                      testID={tID('ProviderCalendarPopover-client-name')}
                      size={TitleSize.MEDIUM}
                      style={{ color: theme.colors.linkDefault }}
                      text={clientNameText}
                    />
                  }
                />
              )}
              <Title
                testID={tID('ProviderCalendarPopover-session-number')}
                size={TitleSize.MEDIUM}
                text={
                  <FormattedMessage
                    defaultMessage='• {sessionNumber}'
                    description='Client full name displayed in calendar event'
                    values={{
                      sessionNumber: extendedProps.isNoShow
                        ? 'No Show'
                        : extendedProps.appointmentSessionNumber === 1 && extendedProps.appointmentClass !== 'recurring'
                          ? 'Intake'
                          : `Session ${extendedProps.appointmentSessionNumber}`,
                    }}
                  />
                }
              />
            </HeadingTitleContainer>
          )}
          {isTeenLedBooking && isLyraSession && appointmentBookedDetails?.clientDob && (
            <BodyText
              size={BodyTextSize.SMALL}
              color={theme.colors.textSecondary}
              text={
                <FormattedMessage
                  defaultMessage='Age {age}'
                  description="Client's age displayed in calendar popover header"
                  values={{
                    age: getAge({ date: appointmentBookedDetails.clientDob.replace('DOB: ', '') }),
                  }}
                />
              }
            />
          )}
        </Header>
        <Section testID={tID('ProviderCalendarPopover-section')}>
          {!(displayAppointmentBookedDetails && isLyraSession) && (
            <>
              <SectionItem testID={tID('ProviderCalendarPopover-sessionTime')}>
                <ScheduleIcon size={24} fillColor={theme.colors.iconDefault} />
                <View>
                  <BodyText
                    size={BodyTextSize.SMALL}
                    text={
                      isTemplate ? (
                        formatRange(eventStartStr, eventEndStr, {
                          hour: 'numeric',
                          minute: 'numeric',
                          timeZone,
                          weekday: 'short',
                        })
                      ) : (
                        <FormattedDateTimeRange
                          from={parseISO(startDate)}
                          to={parseISO(endDate)}
                          weekday='short'
                          month='long'
                          day='numeric'
                          hour='numeric'
                          minute='numeric'
                          timeZoneName='short'
                          timeZone={timeZone}
                        />
                      )
                    }
                  />
                  {clientTimeZone && clientTimeZone !== timeZone && (
                    <BodyText
                      size={BodyTextSize.SMALL}
                      color={theme.colors.textSecondary}
                      text={
                        <FormattedMessage
                          defaultMessage="{firstName}'s local time · {timeRange}"
                          description="Client's local time for the session in their timezone"
                          values={{
                            firstName: clientFirstName ?? '',
                            timeRange: (
                              <FormattedDateTimeRange
                                from={parseISO(startDate)}
                                to={parseISO(endDate)}
                                hour='numeric'
                                minute='numeric'
                                timeZoneName='short'
                                timeZone={clientTimeZone}
                              />
                            ),
                          }}
                        />
                      }
                    />
                  )}
                </View>
              </SectionItem>
              {showModalitySelection &&
                (extendedProps.lyraEventType === 'bookable' ||
                  extendedProps.lyraEventType === 'bookable_recurring') && (
                  <>
                    {extendedProps.modality_availability?.video?.available && (
                      <SectionItem>
                        <VideoIcon fillColor={theme.colors.iconDefault} size={24} />
                        <BodyText
                          size={BodyTextSize.SMALL}
                          text={
                            isDAEngageOnOffPlatformVideoEnabled && supportsOnOffPlatformVideo ? (
                              <FormattedMessage
                                defaultMessage='Video ({platformName})'
                                values={{
                                  platformName:
                                    CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[
                                      extendedProps.modality_availability?.video?.platform ?? DEFAULT_VIDEO_PLATFORM
                                    ],
                                }}
                                description='Video modality with platform label in popover'
                              />
                            ) : (
                              <FormattedMessage defaultMessage='Video' description='Video modality label in popover' />
                            )
                          }
                        />
                      </SectionItem>
                    )}
                    {extendedProps.modality_availability?.in_person?.available && (
                      <SectionItemTopAligned>
                        <OnsiteIcon fillColor={theme.colors.iconDefault} size={24} />
                        <TextColumn>
                          <BodyText
                            size={BodyTextSize.SMALL}
                            text={
                              <FormattedMessage
                                defaultMessage='In-person'
                                description='In-person modality label in popover'
                              />
                            }
                          />
                          {inPersonFullAddress && (
                            <BodyText size={BodyTextSize.SMALL} color='#4F4D4A' text={inPersonFullAddress} />
                          )}
                        </TextColumn>
                      </SectionItemTopAligned>
                    )}
                  </>
                )}
            </>
          )}
          {sections}
        </Section>
        {eventType === 'generic' && !hideCalendarEventDetails && extendedProps.hasConferenceData && (
          <JoinButton
            testID={tID('ProviderCalendarPopover-join-zoom-button')}
            fullWidth
            text={<FormattedMessage defaultMessage='Join' description='Button to join zoom meeting' />}
            onPress={() => onOpenVideoConferencing?.(extendedProps.externalId, extendedProps.calendarConfigurationId)}
          />
        )}
      </Container>
    </>
  )
}
